import React, {useState} from 'react';

import notifications from 'notifications';
import {processTaskSchemaURL} from 'processes/dao/ProcessSchemaDAO';
import {NO_ACCESS_TEXT} from 'processes/ProcessConstants';
import {UpdateRequestV2DefinitionParams} from 'processes/requests_v2/network/RequestV2DefinitionDAO';
import {updateAndCacheRequestV2Definition} from 'processes/requests_v2/request-v2-definition/RequestV2DefinitionPageQueryUtils';
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_REQUEST_DEFINITION_PREFIX_LENGTH,
  RequestTemplateInputGroup,
  maybeStrLengthValidationMessage,
  RequestV2DefinitionDescriptionField,
  RequestV2DefinitionPrefixField,
} from 'processes/requests_v2/request-v2-definition/RequestV2DefinitionUtils';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import {omit} from 'util/omitKey';

export interface ProcessTaskSchemaDetails {
  id: string;
  name: string;
}

export interface EditRequestV2TemplateModalProps {
  onRequestClose: () => void;
  prefix: string;
  requestV2DefinitionId: string;
  description: string | null;
  processTaskSchema: ProcessTaskSchemaDetails | null;
}

export default function EditRequestV2TemplateModal(props: EditRequestV2TemplateModalProps): JSX.Element {
  const initialPrefix = props.prefix;
  const [description, setDescription] = useState(props.description ?? '');
  const [prefix, setPrefix] = useState(props.prefix);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function isSubmitEnabled(): boolean {
    return (
      maybeStrLengthValidationMessage(description, MAX_DESCRIPTION_LENGTH) == null &&
      prefix.trim().length > 0 &&
      maybeStrLengthValidationMessage(prefix, MAX_REQUEST_DEFINITION_PREFIX_LENGTH) == null
    );
  }

  function checkLength(inputStr: string, propertyName: string, maxLength: number): void {
    const maybeErrorMessage = maybeStrLengthValidationMessage(inputStr, maxLength);

    if (maybeErrorMessage != null) {
      setErrors({
        ...errors,
        [propertyName]: maybeErrorMessage,
      });
    } else {
      setErrors({...omit(errors, propertyName)});
    }
  }

  return (
    <Modal
      title="Template information"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={async () => {
        setErrors({});
        try {
          const trimmedPrefix = prefix.trim();
          const updateParams: UpdateRequestV2DefinitionParams = {
            id: props.requestV2DefinitionId,
            description: description.length > 0 ? description.trim() : undefined,
          };
          if (trimmedPrefix !== initialPrefix) {
            updateParams.prefix = trimmedPrefix;
          }
          await updateAndCacheRequestV2Definition(updateParams, 'Updating request template...');
          props.onRequestClose();
          // eslint-disable-next-line benchlint/always-throw-from-catch
        } catch (e) {
          if (e.responseJSON?.errors != null) {
            setErrors(e.responseJSON.errors);
          } else {
            notifications.notifyError(e);
          }
        }
      }}
      buttons={[
        {text: 'Cancel', onClick: props.onRequestClose},
        {text: 'Save', submit: true, disabled: !isSubmitEnabled()},
      ]}
      dataTestAttrs={{component: 'EditRequestV2TemplateModal', element: 'modal'}}
    >
      <FormGroup dataTestAttrs={{component: 'EditRequestV2TemplateModal', element: 'name'}}>
        <RequestTemplateInputGroup>
          <Label>Task Schema</Label>
          {props.processTaskSchema == null ? (
            <div>{NO_ACCESS_TEXT}</div>
          ) : (
            <div>
              <a
                href={processTaskSchemaURL(props.processTaskSchema.id)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {props.processTaskSchema.name}
                &nbsp;
                <BenchlingIcon icon="arrow-up-right-to-square" className={undefined} />
              </a>
            </div>
          )}
        </RequestTemplateInputGroup>
        <RequestV2DefinitionPrefixField
          prefix={prefix}
          onChangeHandler={(e) => {
            setPrefix(e.target.value);
            checkLength(e.target.value, 'prefix', MAX_REQUEST_DEFINITION_PREFIX_LENGTH);
          }}
          errorMessage={errors?.prefix}
          dataTestComponentName="EditRequestV2TemplateModal"
        />
        <RequestV2DefinitionDescriptionField
          description={description}
          onChangeHandler={(e) => {
            setDescription(e.target.value);
            checkLength(e.target.value, 'description', MAX_DESCRIPTION_LENGTH);
          }}
          errorMessage={errors?.description}
          dataTestComponentName="EditRequestV2TemplateModal"
        />
      </FormGroup>
    </Modal>
  );
}
